import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import TerroirErrorBoundary from './redesign/components/ErrorBoundary';
import CookieConsent from './redesign/components/CookieConsent';
import ConfirmDialogProvider from './redesign/components/ConfirmDialog';

// ── Auth pages (Terroir) ─────────────────────────────────────────
import Login from './redesign/pages/auth/Login';
import Register from './redesign/pages/auth/Register';
import ForgotPassword from './redesign/pages/auth/ForgotPassword';
import ResetPassword from './redesign/pages/auth/ResetPassword';
import VerifyEmail from './redesign/pages/auth/VerifyEmail';
import RestoreAccount from './redesign/pages/auth/RestoreAccount';

// ── Legacy pages (zostają do iteracyjnej migracji) ───────────────

// ── Terroir pages (nowy design - active produkcyjnie) ────────────
import Today from './redesign/pages/Today';
import Plot from './redesign/pages/Plot';
import Plants from './redesign/pages/Plants';
import PlantDetail from './redesign/pages/PlantDetail';
import Calendar from './redesign/pages/Calendar';
import Reminders from './redesign/pages/Reminders';
import Journal from './redesign/pages/Journal';
import Planner, { WhatToPlantPage } from './redesign/pages/Planner';
import FriendsPage from './redesign/pages/Friends';
import FeedPage from './redesign/pages/Feed';
import ListingsPage from './redesign/pages/Listings';
import FriendGardenPage from './redesign/pages/FriendGarden';
import AddTask from './redesign/pages/AddTask';
import Menu from './redesign/pages/Menu';
import Gallery from './redesign/pages/Gallery';
import PhotoView from './redesign/pages/PhotoView';
import Capture from './redesign/pages/Capture';
import ProfileHub from './redesign/pages/profile/ProfileHub';
import ProfileGarden from './redesign/pages/profile/ProfileGarden';
import ProfilePublic from './redesign/pages/profile/ProfilePublic';
import ProfileAccount from './redesign/pages/profile/ProfileAccount';
import ProfileStats from './redesign/pages/profile/ProfileStats';
import CareSprays from './redesign/pages/care/CareSprays';
import CareFertilization from './redesign/pages/care/CareFertilization';
import CareAdd from './redesign/pages/care/CareAdd';
import ScheduleBloom from './redesign/pages/garden-tasks/ScheduleBloom';
import ScheduleSeasonal from './redesign/pages/garden-tasks/ScheduleSeasonal';
import ScheduleRoutine from './redesign/pages/garden-tasks/ScheduleRoutine';
import TasksPage from './redesign/pages/Tasks';
import SeedInventory from './redesign/pages/SeedInventory';
import MyVarieties from './redesign/pages/MyVarieties';
import SeedlingPipeline from './redesign/pages/SeedlingPipeline';
import ExportPage from './redesign/pages/Export';
import GardenPlanDetailPage from './redesign/pages/GardenPlanDetail';
import Weather from './redesign/pages/Weather';
import PlantProblems from './redesign/pages/PlantProblems';
import PlantProblemDetail from './redesign/pages/PlantProblemDetail';
import PrivacyPolicyT from './redesign/pages/PrivacyPolicy';
import HelpT from './redesign/pages/Help';
import NotFoundT from './redesign/pages/NotFound';
import PublicProfileT from './redesign/pages/PublicProfile';

import GlobalSearch from './components/GlobalSearch';

// Inicjalizacja i18n na poziomie App (żeby wszystkie ekrany Terroir miały dostęp)
import './redesign/i18n';
// Tokens Terroir (CSS variables scoped do .redesign-root) - musi być załadowane
// dla nowych ekranów również poza RedesignRoot.
import './redesign/tokens.css';
// UXB — ustaw motyw (data-theme) PRZED renderem, żeby nie było migotania.
import { initTheme } from './redesign/lib/theme';
initTheme();

// UXC — lazy-loaded (rzadkie/ciężkie trasy → mniejszy main chunk; opakowane w <Suspense>)
const AdminImagesT = React.lazy(() => import('./redesign/pages/admin/AdminImages'));
const MonthlyGarden = React.lazy(() => import('./redesign/pages/MonthlyGarden'));
const Onboarding = React.lazy(() => import('./redesign/pages/Onboarding'));
const AnalyticsPage = React.lazy(() => import('./redesign/pages/Analytics'));
const YearInGarden = React.lazy(() => import('./redesign/pages/YearInGarden'));
const GardenPlannerPage = React.lazy(() => import('./redesign/pages/GardenPlanner'));
const PlantingWizard = React.lazy(() => import('./redesign/pages/PlantingWizard'));
const AdminDashboard = React.lazy(() => import('./redesign/pages/admin/AdminDashboard'));
const AdminUsers = React.lazy(() => import('./redesign/pages/admin/AdminUsers'));
const AdminPlantsT = React.lazy(() => import('./redesign/pages/admin/AdminPlants'));
const AdminPhotoReviewsT = React.lazy(() => import('./redesign/pages/admin/AdminPhotoReviews'));
const AdminSystemHealth = React.lazy(() => import('./redesign/pages/admin/AdminSystemHealth'));
const AdminEmailTest = React.lazy(() => import('./redesign/pages/admin/AdminEmailTest'));
const AdminBackups = React.lazy(() => import('./redesign/pages/admin/AdminBackups'));
const AdminModeration = React.lazy(() => import('./redesign/pages/admin/AdminModeration'));
const AdminAudit = React.lazy(() => import('./redesign/pages/admin/AdminAudit'));
const AdminOps = React.lazy(() => import('./redesign/pages/admin/AdminOps'));
const AdminAlerts = React.lazy(() => import('./redesign/pages/admin/AdminAlerts'));
const AdminAnalytics = React.lazy(() => import('./redesign/pages/admin/AdminAnalytics'));
const AdminAnnouncements = React.lazy(() => import('./redesign/pages/admin/AdminAnnouncements'));


// ── Protected Route TERROIR (BEZ Layout wrap - Layout jest w komponencie) ─
// Używana dla nowych redesign/pages/*.jsx które MAJĄ <Layout> w środku
const ProtectedRouteTerroir = ({ children, forceRender }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading && !forceRender) {
    return <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: 'var(--t-bg, #0f1410)', color: 'var(--t-text, #e8e6dd)',
      fontFamily: "'DM Sans', sans-serif",
    }}>Ładowanie…</div>;
  }
  if (forceRender && loading) {
    return <Navigate to="/login" />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Layout jest wewnątrz każdego ekranu Terroir - tylko opakowanie .redesign-root
  // (CSS scope) + Terroir-styled ErrorBoundary (legacy psuł spójność designu)
  return (
    <div className="redesign-root">
      <TerroirErrorBoundary>{children}</TerroirErrorBoundary>
    </div>
  );
};

// ── Protected Admin Route TERROIR (sprawdza rolę admin) ──────────
const ProtectedAdminRouteTerroir = ({ children, forceRender }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading && !forceRender) {
    return <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: 'var(--t-bg, #0f1410)', color: 'var(--t-text, #e8e6dd)',
      fontFamily: "'DM Sans', sans-serif",
    }}>Ładowanie…</div>;
  }
  if (forceRender && loading) return <Navigate to="/login" />;
  if (!isAuthenticated) return <Navigate to="/login" />;

  const isAdmin = user?.role === 'admin' || user?.username === 'admin';
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="redesign-root">
      <TerroirErrorBoundary>{children}</TerroirErrorBoundary>
    </div>
  );
};

// ── Public Route (redirect to dashboard if already logged in) ────
const PublicRoute = ({ children, forceRender }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading && !forceRender) {
    return <div className="flex items-center justify-center min-h-screen">Ładowanie...</div>;
  }
  if (forceRender && loading) {
    return children;
  }
  if (!isAuthenticated) {
    return children;
  }

  const isAdmin = user?.role === 'admin' || user?.username === 'admin';
  return <Navigate to={isAdmin ? '/admin' : '/dashboard'} />;
};

// ── Redirect /plots/:id/edit → /plots/:id?edit=1 (legacy) ───────
const PlotEditRedirect = () => {
  const params = window.location.pathname.match(/^\/plots\/(\d+)\/edit$/);
  const id = params ? params[1] : null;
  return <Navigate to={id ? `/plots/${id}?edit=1` : '/plots'} replace />;
};

// ── Default redirect ─────────────────────────────────────────────
const DefaultRedirect = () => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  const isAdmin = user?.role === 'admin' || user?.username === 'admin';
  return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;
};

function AppRoutes() {
  const { loading, isAuthenticated } = useAuth();
  const [forceRender, setForceRender] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Safety timeout: If loading takes too long, force render the app
  useEffect(() => {
    if (loading && !forceRender) {
      const routeTimeout = setTimeout(() => {
        console.error('🚨 Route loading timeout - forcing app to render');
        setForceRender(true);
      }, 5000);
      return () => clearTimeout(routeTimeout);
    }
    if (!loading && forceRender) {
      setForceRender(false);
    }
  }, [loading, forceRender]);

  // Global search (Ctrl+K / Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (isAuthenticated) setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAuthenticated]);

  return (
    <Router>
      {isAuthenticated && (
        <GlobalSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />
      )}

      <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: 'var(--t-text-dim, #999)', fontFamily: "'DM Sans', sans-serif" }}>Ładowanie…</div>}>
      <Routes>
        {/* ===== PUBLIC AUTH (legacy - do migracji w fazie B) ===== */}
        <Route path="/login"    element={<PublicRoute forceRender={forceRender}><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute forceRender={forceRender}><Register /></PublicRoute>} />
        <Route path="/forgot-password"          element={<ForgotPassword />} />
        <Route path="/reset-password/:token"    element={<ResetPassword />} />
        <Route path="/verify-email/:token"      element={<VerifyEmail />} />
        <Route path="/restore-account/:token"   element={<RestoreAccount />} />

        {/* ===== TERROIR ekrany (nowy design - PRODUKCJA) ===== */}
        <Route path="/dashboard"     element={<ProtectedRouteTerroir forceRender={forceRender}><Today /></ProtectedRouteTerroir>} />
        <Route path="/plots"         element={<ProtectedRouteTerroir forceRender={forceRender}><Plot /></ProtectedRouteTerroir>} />
        <Route path="/plots/:id"     element={<ProtectedRouteTerroir forceRender={forceRender}><Plot /></ProtectedRouteTerroir>} />
        <Route path="/plots/:plotId/wizard" element={<ProtectedRouteTerroir forceRender={forceRender}><PlantingWizard /></ProtectedRouteTerroir>} />
        <Route path="/plants"        element={<ProtectedRouteTerroir forceRender={forceRender}><Plants /></ProtectedRouteTerroir>} />
        <Route path="/plants/:id"    element={<ProtectedRouteTerroir forceRender={forceRender}><PlantDetail /></ProtectedRouteTerroir>} />
        <Route path="/calendar"      element={<ProtectedRouteTerroir forceRender={forceRender}><Calendar /></ProtectedRouteTerroir>} />
        <Route path="/reminders"     element={<ProtectedRouteTerroir forceRender={forceRender}><Reminders /></ProtectedRouteTerroir>} />
        <Route path="/journal"       element={<ProtectedRouteTerroir forceRender={forceRender}><Journal /></ProtectedRouteTerroir>} />
        <Route path="/planner"       element={<ProtectedRouteTerroir forceRender={forceRender}><Planner /></ProtectedRouteTerroir>} />
        <Route path="/co-posadzic"   element={<ProtectedRouteTerroir forceRender={forceRender}><WhatToPlantPage /></ProtectedRouteTerroir>} />
        <Route path="/kalendarz-ogrodnika" element={<ProtectedRouteTerroir forceRender={forceRender}><MonthlyGarden /></ProtectedRouteTerroir>} />
        <Route path="/znajomi"          element={<ProtectedRouteTerroir forceRender={forceRender}><FriendsPage /></ProtectedRouteTerroir>} />
        <Route path="/feed"             element={<ProtectedRouteTerroir forceRender={forceRender}><FeedPage /></ProtectedRouteTerroir>} />
        <Route path="/gielda"          element={<ProtectedRouteTerroir forceRender={forceRender}><ListingsPage /></ProtectedRouteTerroir>} />
        <Route path="/tasks/new"     element={<ProtectedRouteTerroir forceRender={forceRender}><AddTask /></ProtectedRouteTerroir>} />
        <Route path="/menu"          element={<ProtectedRouteTerroir forceRender={forceRender}><Menu /></ProtectedRouteTerroir>} />
        <Route path="/onboarding"    element={<ProtectedRouteTerroir forceRender={forceRender}><Onboarding /></ProtectedRouteTerroir>} />
        <Route path="/gallery"       element={<ProtectedRouteTerroir forceRender={forceRender}><Gallery /></ProtectedRouteTerroir>} />
        <Route path="/gallery/capture" element={<ProtectedRouteTerroir forceRender={forceRender}><Capture /></ProtectedRouteTerroir>} />
        <Route path="/gallery/:id"   element={<ProtectedRouteTerroir forceRender={forceRender}><PhotoView /></ProtectedRouteTerroir>} />

        {/* ===== LEGACY ekrany (zostają w starym designie - migracja iteracyjna) ===== */}
        {/* Legacy /plots/new + /plots/:id/edit → otwierają modale w Plot.jsx */}
        <Route path="/plots/new"        element={<Navigate to="/plots?add_plot=1" replace />} />
        <Route path="/plots/:id/edit"   element={<PlotEditRedirect />} />
        <Route path="/beds/:bedId/care" element={<Navigate to="/care/add" replace />} />

        {/* ===== Care Hub Terroir (3 podstrony — opryski + nawożenie + nowy) ===== */}
        <Route path="/care"              element={<ProtectedRouteTerroir forceRender={forceRender}><CareSprays /></ProtectedRouteTerroir>} />
        <Route path="/care/fertilization" element={<ProtectedRouteTerroir forceRender={forceRender}><CareFertilization /></ProtectedRouteTerroir>} />
        <Route path="/care/add"          element={<ProtectedRouteTerroir forceRender={forceRender}><CareAdd /></ProtectedRouteTerroir>} />
        {/* Redirects ze starych URL */}
        <Route path="/sprays"           element={<Navigate to="/care" replace />} />
        <Route path="/fertilization"    element={<Navigate to="/care/fertilization" replace />} />

        {/* ===== Schedule Hub Terroir (kwitnienie + sezonowe + bieżące) ===== */}
        <Route path="/garden-tasks"           element={<ProtectedRouteTerroir forceRender={forceRender}><ScheduleBloom /></ProtectedRouteTerroir>} />
        <Route path="/garden-tasks/seasonal"  element={<ProtectedRouteTerroir forceRender={forceRender}><ScheduleSeasonal /></ProtectedRouteTerroir>} />
        <Route path="/garden-tasks/routine"   element={<ProtectedRouteTerroir forceRender={forceRender}><ScheduleRoutine /></ProtectedRouteTerroir>} />
        {/* Redirects ze starych URL */}
        <Route path="/bloom-timeline"    element={<Navigate to="/garden-tasks" replace />} />
        <Route path="/winter-protection" element={<Navigate to="/garden-tasks/seasonal" replace />} />
        <Route path="/propagation"       element={<Navigate to="/garden-tasks/seasonal" replace />} />
        <Route path="/deadheading"       element={<Navigate to="/garden-tasks/routine" replace />} />
        <Route path="/succession"        element={<Navigate to="/garden-tasks/routine" replace />} />
        {/* ===== Faza E — Tasks / Analytics / Export / Planner Terroir ===== */}
        <Route path="/tasks"             element={<ProtectedRouteTerroir forceRender={forceRender}><TasksPage /></ProtectedRouteTerroir>} />
        <Route path="/nasiona"           element={<ProtectedRouteTerroir forceRender={forceRender}><SeedInventory /></ProtectedRouteTerroir>} />
        <Route path="/moje-odmiany"      element={<ProtectedRouteTerroir forceRender={forceRender}><MyVarieties /></ProtectedRouteTerroir>} />
        <Route path="/rozsada"           element={<ProtectedRouteTerroir forceRender={forceRender}><SeedlingPipeline /></ProtectedRouteTerroir>} />
        <Route path="/analytics"         element={<ProtectedRouteTerroir forceRender={forceRender}><AnalyticsPage /></ProtectedRouteTerroir>} />
        <Route path="/rok"             element={<ProtectedRouteTerroir forceRender={forceRender}><YearInGarden /></ProtectedRouteTerroir>} />
        <Route path="/export"            element={<ProtectedRouteTerroir forceRender={forceRender}><ExportPage /></ProtectedRouteTerroir>} />
        <Route path="/garden-planner"    element={<ProtectedRouteTerroir forceRender={forceRender}><GardenPlannerPage /></ProtectedRouteTerroir>} />
        <Route path="/garden-planner/:id" element={<ProtectedRouteTerroir forceRender={forceRender}><GardenPlanDetailPage /></ProtectedRouteTerroir>} />
        <Route path="/pogoda"            element={<ProtectedRouteTerroir forceRender={forceRender}><Weather /></ProtectedRouteTerroir>} />
        <Route path="/weather"           element={<Navigate to="/pogoda" replace />} />
        <Route path="/problemy"          element={<ProtectedRouteTerroir forceRender={forceRender}><PlantProblems /></ProtectedRouteTerroir>} />
        <Route path="/problemy/:id"      element={<ProtectedRouteTerroir forceRender={forceRender}><PlantProblemDetail /></ProtectedRouteTerroir>} />
        <Route path="/choroby"           element={<Navigate to="/problemy" replace />} />
        {/* Aliasy / redirecty dla URL'i wpisanych ręcznie albo z poprzednich wersji UI */}
        <Route path="/planer"                element={<Navigate to="/garden-planner" replace />} />
        <Route path="/pielegnacja"           element={<Navigate to="/care" replace />} />
        <Route path="/profile/notifications" element={<Navigate to="/profile/account" replace />} />
        <Route path="/help"                  element={<Navigate to="/pomoc" replace />} />

        {/* ===== Profile Hub Terroir (5 podstron) ===== */}
        <Route path="/profile"          element={<ProtectedRouteTerroir forceRender={forceRender}><ProfileHub /></ProtectedRouteTerroir>} />
        <Route path="/profile/garden"   element={<ProtectedRouteTerroir forceRender={forceRender}><ProfileGarden /></ProtectedRouteTerroir>} />
        <Route path="/profile/public"   element={<ProtectedRouteTerroir forceRender={forceRender}><ProfilePublic /></ProtectedRouteTerroir>} />
        <Route path="/profile/account"  element={<ProtectedRouteTerroir forceRender={forceRender}><ProfileAccount /></ProtectedRouteTerroir>} />
        <Route path="/profile/stats"    element={<ProtectedRouteTerroir forceRender={forceRender}><ProfileStats /></ProtectedRouteTerroir>} />

        {/* ===== ADMIN Terroir (Faza F) ===== */}
        <Route path="/admin"               element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminDashboard /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/users"         element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminUsers /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/moderation"    element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminModeration /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/audit"         element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminAudit /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/ops"           element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminOps /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/alerts"        element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminAlerts /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/analytics"     element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminAnalytics /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/announcements" element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminAnnouncements /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/plants"        element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminPlantsT /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/photo-reviews" element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminPhotoReviewsT /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/system-health" element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminSystemHealth /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/email-test"    element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminEmailTest /></ProtectedAdminRouteTerroir>} />
        <Route path="/admin/backups"       element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminBackups /></ProtectedAdminRouteTerroir>} />
        {/* Stare admin URL-e — przekierowania */}
        <Route path="/admin/photo-review"  element={<Navigate to="/admin/photo-reviews" replace />} />
        <Route path="/admin/images"        element={<ProtectedAdminRouteTerroir forceRender={forceRender}><AdminImagesT /></ProtectedAdminRouteTerroir>} />

        {/* ===== Public pages Terroir (bez auth) ===== */}
        <Route path="/privacy-policy" element={<PrivacyPolicyT />} />
        <Route path="/pomoc"          element={<HelpT />} />
        <Route path="/g/:username"    element={<PublicProfileT />} />
        <Route path="/ogrod-znajomego/:userId" element={<ProtectedRouteTerroir forceRender={forceRender}><FriendGardenPage /></ProtectedRouteTerroir>} />

        {/* ===== Sandbox (alias - Terroir podgląd) ===== */}

        {/* ===== Default + 404 ===== */}
        <Route path="/" element={<DefaultRedirect />} />
        <Route path="*" element={<NotFoundT />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <AppRoutes />
            <CookieConsent />
            <ConfirmDialogProvider />
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
